import mobileExportColumns from 'components/tables/mobile-export-columns';
import { useDepartmentText } from 'model/DepartmentTerminology';
import useDepartmentUiMode from 'model/useDepartmentUiMode';
import { Alert, Box, Grid, Group, Loader, Menu, Modal, Portal, Stack, ThemeIcon } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconArchive,
  IconBorderAll,
  IconCalendar,
  IconCalendarEvent,
  IconChevronLeft,
  IconChevronRight,
  IconPlayerPlay,
  IconPlus,
  IconRefresh,
} from '@tabler/icons-react';
import { ColDef } from 'ag-grid-community';
import { useApi } from 'api/api-context';
import Button from 'components/Button';
import ActionIcon from 'components/ActionIcon';
import HrExecuteTermModal from 'components/modals/hr-execute-term/HrExecuteTermModal';
import HrPairCertificateModal from 'components/modals/hr-pair-certificate/HrPairCertificateModal';
import HrReactivateTermModal from 'components/modals/hr-reactivate-term/HrReactivateTermModal';
import HrBulkTermActionModal, {
  HrBulkTermAction,
  HrBulkTermActionFormValues,
} from 'components/modals/hr-bulk-term-action/HrBulkTermActionModal';
import { useConfirm } from 'components/modals/message/MessageProvider';
import DataTable from 'components/tables/DataTable';
import { DataTableExport } from 'components/tables/ExportModal';
import SimpleValueFormatter from 'components/tables/SimpleValueFormatter';
import { hrActionCellVersion, HrTermActions } from 'components/tables/data/hr/HrActions';
import {
  HrDeadlineCell,
  HrDateCell,
  HrIdCell,
  HrLongTextCell,
  HrOrganizationCell,
  HrPersonReferenceCell,
  HrTermTypeCell,
  HrTermSummaryCell,
  HrTextCell,
} from 'components/tables/data/hr/HrTableCells';
import DateFilter, { DateColumnFilter } from 'components/tables/filters/DateFilter';
import OrganizationSelectFilter from 'components/tables/filters/OrganizationSelectFilter';
import useObjectColumn from 'components/tables/hierarchy/ObjectColumn';
import { HrSelectFloatingFilter } from 'components/tables/filters/hr/HrFloatingFilters';
import { HrPersonSelectFilter, HrTermTypeSelectFilter } from 'components/tables/filters/hr/HrEntitySelectFilters';
import {
  canonicalHrTermIds,
  HrIdSetColumnFilter,
  HrIdSetFloatingFilter,
} from 'components/tables/filters/hr/HrIdSetFilter';
import H2Medium from 'components/typography/H2Medium';
import P1Regular from 'components/typography/P1Regular';
import P2Regular from 'components/typography/P2Regular';
import P4Medium from 'components/typography/P4Medium';
import P4Regular from 'components/typography/P4Regular';
import panic from 'errors/panic';
import {
  getResponsiveActionsColumnWidth,
  SINGLE_ACTIONS_COLUMN_WIDTH,
  useResponsiveDesign,
} from 'hooks/use-responsive-design';
import DashboardLayout from 'layouts/dashboard/DashboardLayout';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';
import { getEvidenceFieldValue } from 'utils/evidence-field';
import {
  formatHrDeadlineState,
  formatHrRecurrence,
  formatHrRecurrenceUnit,
  resolveHrDeadlineState,
} from 'pages/hr-module/hr-formatters';
import { HR_BULK_CREATE_TERMS_PAGE_PATH } from 'routes/paths';

const HR_BULK_ACTIONS = [
  { action: 'execute', label: 'Vykonať', modalTitle: 'Hromadné vykonanie termínov', icon: IconPlayerPlay },
  {
    action: 'set-due-date',
    label: 'Nastaviť termín',
    modalTitle: 'Hromadné nastavenie termínu',
    icon: IconCalendarEvent,
  },
  { action: 'archive', label: 'Vyradiť', modalTitle: 'Hromadné vyradenie termínov', icon: IconArchive },
  {
    action: 'reactivate',
    label: 'Zaradiť',
    modalTitle: 'Hromadné zaradenie termínov',
    icon: IconRefresh,
  },
] as const;

/** Returns stable month bounds and Monday-first day cells for the calendar. */
export function hrCalendarMonth(month: string) {
  const match = month.match(/^(\d{4})-(\d{2})$/);
  const now = new Date();
  const year = match ? Number(match[1]) : now.getFullYear();
  const monthIndex = match ? Number(match[2]) - 1 : now.getMonth();
  const first = new Date(Date.UTC(year, monthIndex, 1));
  const last = new Date(Date.UTC(year, monthIndex + 1, 0));
  const leadingDays = (first.getUTCDay() + 6) % 7;
  const occupiedCells = leadingDays + last.getUTCDate();
  const cellCount = occupiedCells <= 35 ? 35 : 42;
  const cells = Array.from({ length: cellCount }, (_, index) => {
    const day = index - leadingDays + 1;
    return day < 1 || day > last.getUTCDate() ? null : day;
  });

  return {
    key: `${year}-${String(monthIndex + 1).padStart(2, '0')}`,
    dueFrom: first.toISOString().slice(0, 10),
    dueTo: last.toISOString().slice(0, 10),
    cells,
  };
}

/** Restores the AG Grid date predicate encoded in a shareable term-list URL. */
export function hrTermDateFilterModel(searchParams: URLSearchParams) {
  const dueFrom = searchParams.get('dueFrom') ?? '';
  const dueTo = searchParams.get('dueTo') ?? '';
  const configuredType = searchParams.get('dueType');
  if (configuredType === 'blank' || configuredType === 'notBlank') {
    return { filterType: 'date', type: configuredType };
  }
  if (!dueFrom) return null;
  const type = configuredType ?? (dueTo ? 'inRange' : 'equals');
  if (type === 'inRange' && dueTo) {
    return { filterType: 'date', type, dateFrom: dueFrom, dateTo: dueTo };
  }
  if (['equals', 'greaterThan', 'lessThan'].includes(type)) {
    return { filterType: 'date', type, dateFrom: dueFrom };
  }
  return null;
}

/** Converts strict AG Grid date predicates to the inclusive bounds accepted by PHP. */
export function hrTermDateServerBounds(searchParams: URLSearchParams) {
  const dueFrom = searchParams.get('dueFrom') ?? undefined;
  const dueTo = searchParams.get('dueTo') ?? undefined;
  const dueType = searchParams.get('dueType');
  /** Shifts an ISO calendar date without applying the browser's local timezone. */
  const shift = (value: string, days: number) => {
    const date = new Date(`${value}T00:00:00Z`);
    if (Number.isNaN(date.getTime())) return value;
    date.setUTCDate(date.getUTCDate() + days);
    return date.toISOString().slice(0, 10);
  };
  if (dueType === 'equals' && dueFrom) return { dueFrom, dueTo: dueFrom };
  if (dueType === 'greaterThan' && dueFrom) return { dueFrom: shift(dueFrom, 1), dueTo: undefined };
  if (dueType === 'lessThan' && dueFrom) return { dueFrom: undefined, dueTo: shift(dueFrom, -1) };
  if (dueType === 'blank' || dueType === 'notBlank') return { dueFrom: undefined, dueTo: undefined };
  return { dueFrom, dueTo };
}

/** Restores the ID-set predicate encoded in a digest or shareable term-list URL. */
export function hrTermIdsFilterModel(searchParams: URLSearchParams) {
  const filter = canonicalHrTermIds(searchParams.get('termIds') ?? searchParams.get('termId'));
  return filter ? { filterType: 'idSet', type: 'belongsTo', filter } : null;
}

/** HR calendar event styled identically to a device-term calendar event. */
function HrTermCalendarItem({ term, onOpen }: { term: any; onOpen: (term: any) => void }) {
  return (
    <Group
      onClick={() => onOpen(term)}
      p={4}
      noWrap
      spacing={8}
      sx={() => ({
        '&:hover': {
          backgroundColor: '#DCDCE5 !important',
          borderRadius: '4px',
          transition: 'background-color 0.5s',
          cursor: 'pointer',
        },
      })}
    >
      <ThemeIcon color="red.8" size={4} children={undefined} />
      <Box w="100%" style={{ whiteSpace: 'nowrap' }}>
        <P4Regular color="gray.8" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {term.termTypeName} - {term.firstName} {term.lastName}
        </P4Regular>
      </Box>
    </Group>
  );
}

/** Uses the same two-visible-items and overflow-menu treatment as the device calendar. */
function HrTermCalendarItems({ terms, onOpen }: { terms: any[]; onOpen: (term: any) => void }) {
  const visibleTerms = terms.slice(0, 2);
  const remainingTerms = terms.slice(2);

  return (
    <Stack spacing={0} style={{ textAlign: 'left', wordWrap: 'normal' }}>
      {visibleTerms.map((term) => (
        <HrTermCalendarItem key={term.termId} term={term} onOpen={onOpen} />
      ))}
      {remainingTerms.length > 0 && (
        <Menu position="bottom">
          <Menu.Target>
            <Box style={{ cursor: 'pointer' }} p={4}>
              <P4Medium color="red.8">ďalšie termíny ({remainingTerms.length})</P4Medium>
            </Box>
          </Menu.Target>
          <Portal>
            <Menu.Dropdown>
              <Stack spacing={4} p={4}>
                {remainingTerms.map((term) => (
                  <HrTermCalendarItem key={term.termId} term={term} onOpen={onOpen} />
                ))}
              </Stack>
            </Menu.Dropdown>
          </Portal>
        </Menu>
      )}
    </Stack>
  );
}

/** Table/calendar browser with URL state and atomic bulk term operations. */
export default function HrTermsTable() {
  const departmentText = useDepartmentText();
  const { mode: departmentUiMode } = useDepartmentUiMode();
  const { getAction, hasPermissionAnywhere } = useApi();
  const canManageHrAnywhere = hasPermissionAnywhere('manage-hr');
  const { confirm } = useConfirm();
  const { isMobile, tableActionMode } = useResponsiveDesign();
  const objectColumn = useObjectColumn({
    breadcrumbField: 'departmentBreadcrumbs',
    organizationFilterKey: 'organizationId',
    permissionSlug: 'browse-hr',
    legacyColumnIds: ['departmentId'],
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'calendar' ? 'calendar' : 'table';

  const [rows, setRows] = useState<any[]>([]);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [refreshToken, setRefreshToken] = useState(0);
  const [bulkOpened, setBulkOpened] = useState(false);
  const [bulkTargetRows, setBulkTargetRows] = useState<any[]>([]);
  const [executeTerm, setExecuteTerm] = useState<any>(null);
  const [pairTerm, setPairTerm] = useState<any>(null);
  const [reactivateTerm, setReactivateTerm] = useState<any>(null);
  const [evidenceFieldsColumns, setEvidenceFieldsColumns] = useState<ColDef[]>([]);
  const inventoryRequest = useRef(0);
  const [executeFileCategories, setExecuteFileCategories] = useState<any[]>([]);
  const [calendarTerm, setCalendarTerm] = useState<any>(null);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarError, setCalendarError] = useState<string | null>(null);
  const [fileCategories, setFileCategories] = useState<any[]>([]);
  const tableFilterKeys = useRef(new Set<string>());
  const executeCategoryLoadId = useRef(0);
  const calendar = hrCalendarMonth(searchParams.get('month') ?? '');
  const [initialTermIdsFilter] = useState(() => hrTermIdsFilterModel(searchParams));
  const [initialDepartmentId] = useState(() => searchParams.get('departmentId'));
  const [initialFiltersApplied, setInitialFiltersApplied] = useState(false);
  const handleInitialFiltersApplied = useCallback(() => setInitialFiltersApplied(true), []);

  const [bulkAction, setBulkAction] = useState<HrBulkTermAction>('execute');
  const bulkModalTitle =
    HR_BULK_ACTIONS.find(({ action }) => action === bulkAction)?.modalTitle ?? HR_BULK_ACTIONS[0].modalTitle;

  const initialFilters = useMemo(() => {
    const filters: Record<string, any> = {};
    [
      'personId',
      'firstName',
      'lastName',
      'organizationId',
      'departmentName',
      'termTypeId',
      'termTypeName',
      'deadlineState',
      'note',
    ].forEach((key) => {
      const value = searchParams.get(key);
      if (value) {
        const exact = ['termId', 'personId', 'organizationId', 'termTypeId', 'deadlineState'];
        filters[key] = {
          filterType: 'text',
          type: exact.includes(key) ? 'equals' : 'contains',
          filter: value,
        };
      }
    });
    if (!initialFiltersApplied) {
      if (initialTermIdsFilter) filters.termId = initialTermIdsFilter;
      if (initialDepartmentId)
        filters['department.departmentId'] = {
          filter: initialDepartmentId,
          mode: departmentUiMode === 'legacy' ? 'exact' : 'subtree',
        };
    }
    const lifecycleStatus = searchParams.get('status');
    if (lifecycleStatus) {
      filters._notArchived = {
        filterType: 'text',
        type: 'equals',
        filter:
          lifecycleStatus === 'active' || lifecycleStatus === 'completed'
            ? '1'
            : lifecycleStatus === 'archived'
            ? '0'
            : lifecycleStatus,
      };
    }
    const dateFilter = hrTermDateFilterModel(searchParams);
    if (dateFilter) filters.nextDueDate = dateFilter;
    return filters;
  }, [departmentUiMode, initialDepartmentId, initialFiltersApplied, initialTermIdsFilter, searchParams]);

  useEffect(() => {
    if (!searchParams.has('termIds') && !searchParams.has('termId') && !searchParams.has('departmentId')) return;

    setSearchParams(
      (params) => {
        params.delete('termIds');
        params.delete('termId');
        params.delete('departmentId');
        return params;
      },
      { replace: true }
    );
  }, []);

  const action = useCallback(async () => {
    const request = ++inventoryRequest.current;
    const listTerms = getAction('HrListTerms');
    const dateBounds = hrTermDateServerBounds(searchParams);
    const tableMode = tab === 'table';
    const result = await listTerms({
      query: {
        filters: {
          personId: tableMode && searchParams.get('personId') ? Number(searchParams.get('personId')) : undefined,
          termTypeId: tableMode && searchParams.get('termTypeId') ? Number(searchParams.get('termTypeId')) : undefined,
          organizationId:
            tableMode && searchParams.get('organizationId') ? Number(searchParams.get('organizationId')) : undefined,
          firstName: tableMode ? searchParams.get('firstName') ?? undefined : undefined,
          lastName: tableMode ? searchParams.get('lastName') ?? undefined : undefined,
          departmentName: tableMode ? searchParams.get('departmentName') ?? undefined : undefined,
          termTypeName: tableMode ? searchParams.get('termTypeName') ?? undefined : undefined,
          note: tableMode ? searchParams.get('note') ?? undefined : undefined,
          dueFrom: tableMode ? dateBounds.dueFrom : calendar.dueFrom,
          dueTo: tableMode ? dateBounds.dueTo : calendar.dueTo,
          deadlineState: tableMode ? searchParams.get('deadlineState') ?? undefined : undefined,
        },
      },
    });
    if (request !== inventoryRequest.current) return [];
    const evidenceFieldMap = new Map<string, any>();
    const mapped = result.map((term) => {
      const deadlineState = resolveHrDeadlineState(term.deadlineState, term.status);
      const evidenceFields = (term.evidenceFields ?? []).reduce((values: Record<string, any>, field: any) => {
        if (!field.fieldName || !field.evidenceFieldId) return values;
        const key = String(field.evidenceFieldId);
        evidenceFieldMap.set(key, field);
        values[key] = getEvidenceFieldValue(field) ?? '';
        return values;
      }, {});
      return {
        ...term,
        evidenceFields,
        recurrenceQuantity: term.recurrence?.quantity ?? '',
        recurrenceUnit: term.recurrence?.unit ?? '',
        lastExecutionDate: term.lastExecutionDate ?? '',
        _recurrence: formatHrRecurrence(term.recurrence),
        deadlineState,
        rawStatus: term.status,
        _notArchived: term.status === 'archived' ? '0' : '1',
        _summary: `${term.firstName} ${term.lastName} ${term.termTypeName} ${
          term.nextDueDate ?? ''
        } ${formatHrDeadlineState(deadlineState)}`,
        _actions: `${term.termId} ${term.version} ${term.status}`,
      };
    });
    setEvidenceFieldsColumns(
      Array.from(evidenceFieldMap).map(([key, { fieldName, precision = 0 }]) => ({
        field: `evidenceFields.${key}`,
        headerName: fieldName,
        minWidth: 200,
        sortable: true,
        unSortIcon: true,
        filter: true,
        floatingFilter: true,
        filterValueGetter: ({ data }) => data?.evidenceFields?.[key],
        cellRenderer: ({ data }: { data?: any }) => (
          <SimpleValueFormatter value={data?.evidenceFields?.[key]} precision={precision} />
        ),
      }))
    );
    return mapped;
  }, [calendar.dueFrom, calendar.dueTo, getAction, refreshToken, searchParams, tab]);

  useEffect(
    () => () => {
      inventoryRequest.current += 1;
    },
    []
  );

  useEffect(() => {
    if (tab !== 'calendar') {
      return undefined;
    }

    let current = true;
    setCalendarLoading(true);
    setCalendarError(null);
    setRows([]);
    action()
      .then((terms) => {
        if (current) {
          setRows(terms);
        }
      })
      .catch((caught) => {
        if (current) {
          setRows([]);
          setCalendarError('Termíny pre vybraný mesiac sa nepodarilo načítať.');
          panic(caught as Error);
        }
      })
      .finally(() => {
        if (current) {
          setCalendarLoading(false);
        }
      });

    return () => {
      current = false;
    };
  }, [action, tab]);

  /** Removes one term from active use after explicit confirmation. */
  const archiveTerm = useCallback(
    (term: any) => {
      confirm({
        title: 'Vyradiť termín',
        content: `Naozaj si prajete vyradiť termín „${term.termTypeName}“ pre ${term.firstName} ${term.lastName}? História zostane zachovaná.`,
        primaryButtonText: 'Vyradiť',
        secondaryButtonText: 'Zrušiť',
        onConfirm: async () => {
          try {
            await getAction('HrSetTermStatus')({
              parameters: { termId: String(term.termId) },
              payload: { version: term.version, status: 'archived' },
            });
            setCalendarTerm(null);
            setRefreshToken((value) => value + 1);
          } catch (caught) {
            panic(caught as Error);
          }
        },
      });
    },
    [confirm, getAction]
  );

  /** Opens direct term execution in place and loads attachment categories for its organization. */
  const openExecuteTerm = useCallback(
    async (term: any) => {
      const loadId = ++executeCategoryLoadId.current;
      setExecuteFileCategories([]);
      setCalendarTerm(null);
      setExecuteTerm(term);
      if (term.actionPermissions?.accessFiles && term.organizationId) {
        try {
          const categories = await getAction('HrListFileCategories')({
            query: { filters: { organizationId: term.organizationId, status: 'active' } },
          });
          if (loadId === executeCategoryLoadId.current) {
            setExecuteFileCategories(categories);
          }
        } catch (caught) {
          if (loadId === executeCategoryLoadId.current) {
            panic(caught as Error);
          }
        }
      }
    },
    [getAction]
  );

  /** Closes direct execution and invalidates any category response still in flight. */
  const closeExecuteTerm = useCallback(() => {
    executeCategoryLoadId.current += 1;
    setExecuteTerm(null);
    setExecuteFileCategories([]);
  }, []);

  /** Opens term reactivation directly from the table or calendar without navigating to the person form. */
  const openReactivateTerm = useCallback((term: any) => {
    setCalendarTerm(null);
    setReactivateTerm(term);
  }, []);

  /** Unpairs a technician certificate from the global term table after explicit confirmation. */
  const unpairCertificate = useCallback(
    (term: any) => {
      confirm({
        title: 'Odpojiť osvedčenie',
        content: 'Termín sa vyradí, osvedčenie a jeho história zostanú zachované.',
        primaryButtonText: 'Odpojiť',
        secondaryButtonText: 'Zrušiť',
        onConfirm: async () => {
          try {
            await getAction('HrUnpairCertificate')({ parameters: { termId: String(term.termId) } });
            setRefreshToken((value) => value + 1);
          } catch (caught) {
            panic(caught as Error);
          }
        },
      });
    },
    [confirm, getAction]
  );

  const columns = useMemo<ColDef[]>(
    () =>
      isMobile
        ? [
            {
              field: '_summary',
              headerName: 'Termín',
              pinned: 'left',
              lockPosition: 'left',
              minWidth: 155,
              flex: 1,
              autoHeight: true,
              autoHeaderHeight: true,
              cellRenderer: HrTermSummaryCell,
              getQuickFilterText: ({ data }) =>
                [data?.firstName, data?.lastName, data?.organizationName, data?.departmentName, data?.termTypeName]
                  .filter(Boolean)
                  .join(' '),
            },
            {
              field: 'termId',
              headerName: 'ID',
              hide: true,
              filter: HrIdSetColumnFilter,
            },
            {
              field: '_notArchived',
              headerName: 'Stav',
              hide: true,
              filter: true,
            },
            ...mobileExportColumns([
              {
                field: 'personId',
                headerName: 'Osoba',
                hide: true,
                valueFormatter: ({ data }) => `${data?.firstName ?? ''} ${data?.lastName ?? ''}`.trim(),
              },
              {
                field: 'organizationId',
                headerName: 'Organizácia',
                hide: true,
                valueFormatter: ({ data }) => data?.organizationName ?? '',
              },
              {
                field: 'termTypeId',
                headerName: 'Typ termínu',
                hide: true,
                valueFormatter: ({ data }) => data?.termTypeName ?? '',
              },
              {
                field: 'checkedVariants',
                headerName: 'Varianty',
                hide: true,
                valueGetter: ({ data }) => (data?.checkedVariants ?? []).map(({ name }: any) => name).join(', '),
              },
              { field: 'lastExecutionDate', headerName: 'Dátum posledného vykonania', hide: true },
              { field: 'nextDueDate', headerName: 'Najbližší termín', hide: true },
              { field: 'recurrenceQuantity', headerName: 'Opakovanie', hide: true },
              {
                field: 'recurrenceUnit',
                headerName: 'Jednotka opakovania',
                hide: true,
                valueFormatter: ({ value }) => formatHrRecurrenceUnit(value),
              },
              {
                field: 'deadlineState',
                headerName: 'Stav lehoty',
                hide: true,
                valueFormatter: ({ value }) => formatHrDeadlineState(value),
              },
              { field: 'note', headerName: 'Poznámka', hide: true },
            ]),
            ...objectColumn.columns.map((column) => ({ ...column, hide: true })),
            ...evidenceFieldsColumns.map((column) => ({ ...column, hide: true })),
            {
              field: '_actions',
              headerName: 'Akcie',
              valueGetter: hrActionCellVersion,
              pinned: 'right',
              width: getResponsiveActionsColumnWidth(SINGLE_ACTIONS_COLUMN_WIDTH, tableActionMode),
              minWidth: getResponsiveActionsColumnWidth(SINGLE_ACTIONS_COLUMN_WIDTH, tableActionMode),
              maxWidth: getResponsiveActionsColumnWidth(SINGLE_ACTIONS_COLUMN_WIDTH, tableActionMode),
              autoHeaderHeight: true,
              cellRenderer: HrTermActions,
              cellRendererParams: {
                onArchive: archiveTerm,
                onExecute: openExecuteTerm,
                onReactivate: openReactivateTerm,
                showDetail: true,
                onPairCertificate: setPairTerm,
                onUnpairCertificate: unpairCertificate,
              },
            },
          ]
        : [
            {
              field: 'termId',
              headerName: 'ID',
              width: 180,
              minWidth: 180,
              sortable: true,
              unSortIcon: true,
              filter: HrIdSetColumnFilter,
              floatingFilter: true,
              floatingFilterComponent: HrIdSetFloatingFilter,
              autoHeaderHeight: true,
              cellRenderer: HrIdCell,
            },
            {
              field: 'personId',
              headerName: 'Osoba',
              minWidth: 230,
              sortable: true,
              unSortIcon: true,
              filter: true,
              floatingFilter: true,
              floatingFilterComponent: HrPersonSelectFilter,
              autoHeaderHeight: true,
              cellRenderer: HrPersonReferenceCell,
              valueFormatter: ({ data }) => `${data?.firstName ?? ''} ${data?.lastName ?? ''}`.trim(),
              getQuickFilterText: ({ data }) => `${data?.firstName ?? ''} ${data?.lastName ?? ''}`.trim(),
            },
            {
              field: 'organizationId',
              headerName: 'Organizácia',
              minWidth: 240,
              sortable: true,
              unSortIcon: true,
              filter: true,
              floatingFilter: true,
              floatingFilterComponent: OrganizationSelectFilter.configure({
                filterKey: 'organizationId',
                permissionSlug: 'browse-hr',
              }),
              autoHeaderHeight: true,
              cellRenderer: HrOrganizationCell,
              valueFormatter: ({ data }) => data?.organizationName ?? '',
              getQuickFilterText: ({ data }) => data?.organizationName ?? '',
            },
            ...objectColumn.columns,
            {
              field: 'termTypeId',
              headerName: 'Typ termínu',
              minWidth: 240,
              sortable: true,
              unSortIcon: true,
              filter: true,
              floatingFilter: true,
              floatingFilterComponent: HrTermTypeSelectFilter,
              autoHeaderHeight: true,
              cellRenderer: HrTermTypeCell,
              valueFormatter: ({ data }) => data?.termTypeName ?? '',
              getQuickFilterText: ({ data }) => data?.termTypeName ?? '',
            },
            {
              field: 'checkedVariants',
              headerName: 'Varianty',
              minWidth: 240,
              autoHeaderHeight: true,
              valueGetter: ({ data }) => (data?.checkedVariants ?? []).map(({ name }: any) => name).join(', '),
              cellRenderer: HrTextCell,
            },
            {
              field: 'lastExecutionDate',
              headerName: 'Dátum posledného vykonania',
              minWidth: 240,
              sortable: true,
              unSortIcon: true,
              filter: DateColumnFilter,
              floatingFilter: true,
              floatingFilterComponent: DateFilter.configure({ filterKey: 'lastExecutionDate' }),
              autoHeaderHeight: true,
              cellRenderer: HrDateCell,
            },
            {
              field: 'nextDueDate',
              headerName: 'Najbližší termín',
              minWidth: 210,
              sortable: true,
              unSortIcon: true,
              filter: DateColumnFilter,
              floatingFilter: true,
              floatingFilterComponent: DateFilter.configure({ filterKey: 'nextDueDate' }),
              autoHeaderHeight: true,
              cellRenderer: HrDateCell,
            },
            {
              field: '_recurrence',
              headerName: 'Opakovanie',
              minWidth: 180,
              sortable: true,
              unSortIcon: true,
              autoHeaderHeight: true,
              cellRenderer: HrLongTextCell,
            },
            {
              field: 'recurrenceQuantity',
              headerName: 'Opakovanie',
              hide: true,
            },
            {
              field: 'recurrenceUnit',
              headerName: 'Jednotka opakovania',
              hide: true,
              valueFormatter: ({ value }) => formatHrRecurrenceUnit(value),
            },
            {
              field: 'deadlineState',
              headerName: 'Stav lehoty',
              minWidth: 180,
              sortable: true,
              unSortIcon: true,
              filter: true,
              floatingFilter: true,
              floatingFilterComponent: HrSelectFloatingFilter.configure({
                filterKey: 'deadlineState',
                placeholder: 'Stav lehoty',
                data: [
                  { value: 'upcoming', label: 'Nadchádzajúci' },
                  { value: 'overdue', label: 'Po termíne' },
                  { value: 'completed', label: 'Dokončený' },
                ],
              }),
              autoHeaderHeight: true,
              cellRenderer: HrDeadlineCell,
              valueFormatter: ({ value }) => formatHrDeadlineState(value),
            },
            {
              field: '_notArchived',
              headerName: 'Stav',
              hide: true,
              filter: true,
            },
            {
              field: 'note',
              headerName: 'Poznámka',
              minWidth: 280,
              autoHeight: true,
              autoHeaderHeight: true,
              cellRenderer: HrLongTextCell,
            },
            ...evidenceFieldsColumns,
            {
              field: '_actions',
              headerName: 'Akcie',
              valueGetter: hrActionCellVersion,
              pinned: 'right',
              width: getResponsiveActionsColumnWidth(SINGLE_ACTIONS_COLUMN_WIDTH, tableActionMode),
              minWidth: getResponsiveActionsColumnWidth(SINGLE_ACTIONS_COLUMN_WIDTH, tableActionMode),
              maxWidth: getResponsiveActionsColumnWidth(SINGLE_ACTIONS_COLUMN_WIDTH, tableActionMode),
              autoHeaderHeight: true,
              cellRenderer: HrTermActions,
              cellRendererParams: {
                onArchive: archiveTerm,
                onExecute: openExecuteTerm,
                onReactivate: openReactivateTerm,
                showDetail: true,
                onPairCertificate: setPairTerm,
                onUnpairCertificate: unpairCertificate,
              },
            },
          ],
    [
      archiveTerm,
      evidenceFieldsColumns,
      objectColumn.columns,
      isMobile,
      openExecuteTerm,
      openReactivateTerm,
      tableActionMode,
      unpairCertificate,
    ]
  );

  const exportColumns = useMemo(
    () => [
      'termId',
      'personId',
      'organizationId',
      ...objectColumn.columnKeys,
      'termTypeId',
      'checkedVariants',
      'lastExecutionDate',
      'nextDueDate',
      'recurrenceQuantity',
      'recurrenceUnit',
      'deadlineState',
      'note',
      ...evidenceFieldsColumns.map(({ field }) => field ?? '').filter(Boolean),
    ],
    [evidenceFieldsColumns, objectColumn.columnKeys]
  );

  const dataExport: DataTableExport<any> = useMemo(
    () => ({
      modalTitle: 'Exportovať HR termíny',
      fileName: 'hr-terminy.xlsx',
      columnKeys: exportColumns,
      columnReplacements: {
        ...objectColumn.columnReplacements,
        _recurrence: ['recurrenceQuantity', 'recurrenceUnit'],
        _summary: ['personId', 'termTypeId', 'nextDueDate', 'deadlineState'],
      },
      ready: objectColumn.exportReady,
      error: objectColumn.exportError,
      initialSelectedColumns: (data) =>
        exportColumns.filter((column) =>
          column.startsWith('evidenceFields.')
            ? data.some((row) => {
                const value = row.evidenceFields?.[column.replace('evidenceFields.', '')];
                return value !== undefined && value !== null && value !== '';
              })
            : true
        ),
    }),
    [exportColumns, objectColumn.columnReplacements, objectColumn.exportError, objectColumn.exportReady]
  );

  const bulkApplicableRows = useMemo(() => {
    /** Returns whether the selected term is active. */
    const active = (row: any) => (row.rawStatus ?? row.status) === 'active';
    /** Returns whether the selected term is archived and therefore reactivatable. */
    const archived = (row: any) => (row.rawStatus ?? row.status) === 'archived';
    /** Returns whether the selected term may be edited. */
    const editable = (row: any) => Boolean(row.actionPermissions?.edit);
    /** Excludes certificate terms from ordinary-only bulk operations. */
    const ordinary = (row: any) => row.kind !== 'technician-certificate';

    return {
      execute: selectedRows.filter((row) => active(row) && ordinary(row) && row.actionPermissions?.execute),
      'set-due-date': selectedRows.filter((row) => active(row) && editable(row)),
      archive: selectedRows.filter((row) => active(row) && editable(row)),
      reactivate: selectedRows.filter((row) => archived(row) && editable(row)),
    };
  }, [selectedRows]);

  /** Opens one supported bulk operation and loads proof categories only for execution. */
  const openBulk = async (action: keyof typeof bulkApplicableRows) => {
    const targetRows = bulkApplicableRows[action];
    setFileCategories([]);
    setBulkTargetRows(targetRows);
    setBulkAction(action);
    setBulkOpened(true);
    const organizationId = targetRows[0]?.organizationId;
    if (action === 'execute' && organizationId) {
      try {
        const categories = await getAction('HrListFileCategories')({
          query: { filters: { organizationId, status: 'active' } },
        });
        setFileCategories(categories);
      } catch {
        setFileCategories([]);
      }
    }
  };

  /** Closes the bulk dialog without changing the current table selection. */
  const closeBulk = () => {
    setBulkOpened(false);
    setBulkTargetRows([]);
  };

  /** Submits the frozen applicable term subset for the selected bulk operation. */
  const submitBulk = async (values: HrBulkTermActionFormValues) => {
    try {
      const bulk = getAction('HrBulkUpdateTerms');
      const payload: any = {
        action: values.action,
        terms: bulkTargetRows.map((term) => ({ termId: term.termId, version: term.version })),
        note: values.note || undefined,
      };
      if (values.action === 'execute') {
        payload.executedOn = values.executedOn;
        payload.nextDueDate = values.nextDueDate || undefined;
        payload.proofFiles = values.proofFileId
          ? [
              {
                fileId: values.proofFileId,
                fileName: values.proofFileName,
                categoryId: Number(values.proofCategoryId),
              },
            ]
          : undefined;
      } else if (values.action === 'set-due-date' || values.action === 'reactivate') {
        payload.dueDate = values.dueDate || undefined;
      }
      const response = await bulk({
        payload,
      });
      notifications.show({
        title: 'Hromadná operácia bola dokončená',
        message: `Aktualizovaných termínov: ${response.count}.`,
        color: SUCCESS_NOTIFICATION_COLOR,
      });
      setBulkOpened(false);
      setBulkTargetRows([]);
      setSelectedRows([]);
      setRefreshToken((value) => value + 1);
    } catch (caught) {
      panic(caught as Error);
    }
  };

  /** Persists the selected table or calendar mode in the shareable URL. */
  const setTab = (value: 'table' | 'calendar') => {
    setSearchParams((params) => {
      params.set('tab', value);
      if (value === 'calendar') {
        [
          'termId',
          'termIds',
          'personId',
          'firstName',
          'lastName',
          'organizationId',
          'departmentId',
          'departmentName',
          'termTypeId',
          'termTypeName',
          'status',
          'deadlineState',
          'note',
          'dueType',
        ].forEach((key) => params.delete(key));
        params.set('month', calendar.key);
        params.set('dueFrom', calendar.dueFrom);
        params.set('dueTo', calendar.dueTo);
      } else {
        params.delete('month');
        params.delete('dueFrom');
        params.delete('dueTo');
        params.delete('dueType');
      }
      return params;
    });
  };

  /** Moves the calendar and its authoritative due-date query window by one month. */
  const moveCalendarMonth = (offset: number) => {
    const [year, month] = calendar.key.split('-').map(Number);
    const next = new Date(Date.UTC(year, month - 1 + offset, 1));
    const nextCalendar = hrCalendarMonth(`${next.getUTCFullYear()}-${String(next.getUTCMonth() + 1).padStart(2, '0')}`);
    setSearchParams((params) => {
      params.set('tab', 'calendar');
      params.set('month', nextCalendar.key);
      params.set('dueFrom', nextCalendar.dueFrom);
      params.set('dueTo', nextCalendar.dueTo);
      return params;
    });
  };

  /** Mirrors supported table filters into the shareable HR terms URL. */
  const syncTableFilters = (filters: Record<string, any>) => {
    const keys = [
      'personId',
      'firstName',
      'lastName',
      'organizationId',
      'departmentName',
      'termTypeId',
      'termTypeName',
      'status',
      'deadlineState',
      'note',
    ];
    const presentKeys = new Set(
      keys.filter((key) => {
        const value = key === 'status' ? filters._notArchived?.filter : filters[key]?.filter;
        return value !== undefined && value !== null && value !== '';
      })
    );
    if (filters.nextDueDate) presentKeys.add('nextDueDate');
    const managedKeys = new Set([...tableFilterKeys.current, ...presentKeys]);
    setSearchParams((params) => {
      managedKeys.forEach((key) => {
        if (key === 'nextDueDate') {
          const dateFilter = filters.nextDueDate;
          if (!dateFilter) {
            params.delete('dueType');
            params.delete('dueFrom');
            params.delete('dueTo');
            return;
          }
          params.set('dueType', dateFilter.type);
          if (dateFilter.dateFrom) params.set('dueFrom', dateFilter.dateFrom);
          else params.delete('dueFrom');
          if (dateFilter.dateTo) params.set('dueTo', dateFilter.dateTo);
          else params.delete('dueTo');
          return;
        }
        const rawValue = key === 'status' ? filters._notArchived?.filter : filters[key]?.filter;
        const value =
          key === 'status' ? (rawValue === '1' ? 'active' : rawValue === '0' ? 'archived' : rawValue) : rawValue;
        if (value === undefined || value === null || value === '') {
          params.delete(key);
        } else {
          params.set(key, String(value));
        }
      });
      return params;
    });
    tableFilterKeys.current = presentKeys;
  };

  /** Restores the unfiltered calendar to the local current month. */
  const showCurrentMonth = () => {
    const current = hrCalendarMonth('');
    setSearchParams((params) => {
      params.set('tab', 'calendar');
      params.set('month', current.key);
      params.set('dueFrom', current.dueFrom);
      params.set('dueTo', current.dueTo);
      return params;
    });
  };

  return (
    <DashboardLayout
      height="screen"
      title={tab === 'calendar' ? 'HR termíny – kalendár' : undefined}
      breadcrumbs={[{ title: 'Termíny' }]}
      actions={
        tab === 'calendar' ? (
          <Button variant="tertiary" leftIcon={<IconBorderAll />} onClick={() => setTab('table')}>
            Tabuľka
          </Button>
        ) : undefined
      }
    >
      {tab === 'table' ? (
        <DataTable
          tableId="hr-terms"
          title="Termíny"
          toggleDiscardedLabel="Zobraziť vyradené"
          discardedFilterField="_notArchived"
          addButtonText="Pridať termíny"
          addButtonTarget={HR_BULK_CREATE_TERMS_PAGE_PATH.original}
          addButtonIcon={<IconPlus stroke={1.5} />}
          hideAddButton={!canManageHrAnywhere}
          additionalButtons={
            isMobile ? (
              <ActionIcon variant="tertiary" size="md" aria-label="Kalendár" onClick={() => setTab('calendar')}>
                <IconCalendar stroke={1.5} size={20} />
              </ActionIcon>
            ) : (
              <Button variant="tertiary" leftIcon={<IconCalendar />} onClick={() => setTab('calendar')}>
                Kalendár
              </Button>
            )
          }
          columns={columns}
          action={action}
          initialFilters={initialFilters}
          onInitialFiltersApplied={handleInitialFiltersApplied}
          preserveProfileFilterColumnIds={
            !initialFiltersApplied && (initialDepartmentId || initialTermIdsFilter) ? [] : objectColumn.filterColumnKeys
          }
          onFiltersChange={syncTableFilters}
          selectedRows={selectedRows}
          onSelectedRowsChange={setSelectedRows}
          dataRefreshToken={refreshToken}
          bulkActions={
            <Group spacing={8} noWrap>
              {HR_BULK_ACTIONS.filter(({ action }) => bulkApplicableRows[action].length > 0).map(
                ({ action, label, icon: BulkActionIcon }) => {
                  const count = bulkApplicableRows[action].length;
                  return (
                    <Button
                      key={action}
                      variant="primary"
                      size="sm"
                      leftIcon={<BulkActionIcon size={18} stroke={1.5} />}
                      onClick={() => openBulk(action)}
                    >
                      {label} ({count})
                    </Button>
                  );
                }
              )}
            </Group>
          }
          getRowId={({ data }) => String(data.termId)}
          dataExport={dataExport}
          profileColumnAliases={objectColumn.profileColumnAliases}
          profileMigrationReady={objectColumn.profileMigrationReady}
        />
      ) : (
        <Stack p={24} bg="white" style={{ borderRadius: 4 }}>
          <Group spacing={16} position="apart">
            <Group spacing={8}>
              <ActionIcon
                variant="subtle-gray"
                size="md"
                onClick={() => moveCalendarMonth(-1)}
                aria-label="Predchádzajúci mesiac"
              >
                <IconChevronLeft stroke={1.5} size={24} />
              </ActionIcon>
              <ActionIcon
                variant="subtle-gray"
                size="md"
                onClick={() => moveCalendarMonth(1)}
                aria-label="Nasledujúci mesiac"
              >
                <IconChevronRight stroke={1.5} size={24} />
              </ActionIcon>
              <H2Medium>
                {new Date(`${calendar.key}-01T00:00:00Z`).toLocaleDateString('sk-SK', {
                  month: 'long',
                  year: 'numeric',
                  timeZone: 'UTC',
                })}
              </H2Medium>
            </Group>
            <Button variant="tertiary" onClick={showCurrentMonth}>
              Aktuálny mesiac
            </Button>
          </Group>
          {calendarLoading ? (
            <Group position="center" py={48} role="status" aria-label="Načítavam HR kalendár">
              <Loader />
            </Group>
          ) : calendarError ? (
            <Alert color="red" title="HR kalendár sa nepodarilo načítať">
              <Stack spacing={12} align="flex-start">
                <P2Regular>{calendarError}</P2Regular>
                <Button
                  type="button"
                  variant="subtle-gray"
                  leftIcon={<IconRefresh stroke={1.5} size={20} />}
                  onClick={() => setRefreshToken((value) => value + 1)}
                >
                  Načítať znova
                </Button>
              </Stack>
            </Alert>
          ) : isMobile ? (
            <Stack role="list" aria-label={`Agenda HR termínov ${calendar.key}`}>
              {[...rows]
                .filter((term) => typeof term.nextDueDate === 'string')
                .sort((left, right) => left.nextDueDate.localeCompare(right.nextDueDate))
                .map((term) => (
                  <Stack key={term.termId} spacing={4} role="listitem">
                    <HrDateCell value={term.nextDueDate} />
                    <HrTermCalendarItem term={term} onOpen={setCalendarTerm} />
                  </Stack>
                ))}
            </Stack>
          ) : (
            <Stack spacing={0} role="grid" aria-label={`Kalendár HR termínov ${calendar.key}`}>
              <Grid columns={7} style={{ textAlign: 'center' }} m={0}>
                {['Pondelok', 'Utorok', 'Streda', 'Štvrtok', 'Piatok', 'Sobota', 'Nedeľa'].map((day) => (
                  <Grid.Col key={day} span={1} m={0} p={0}>
                    <P2Regular>{day}</P2Regular>
                  </Grid.Col>
                ))}
              </Grid>
              <Grid columns={7} m={0} style={{ border: '1px solid #DCDCE5' }}>
                {calendar.cells.map((day, index) => {
                  const date = day === null ? null : `${calendar.key}-${String(day).padStart(2, '0')}`;
                  const dayTerms = date ? rows.filter((term) => term.nextDueDate === date) : [];

                  return (
                    <Grid.Col
                      key={`${calendar.key}-${index}`}
                      role="gridcell"
                      span={1}
                      p={8}
                      style={{
                        minHeight: calendar.cells.length > 35 ? '11.8vh' : '13.6vh',
                        border: '1px solid #DCDCE5',
                      }}
                    >
                      {day !== null && <P1Regular>{day}</P1Regular>}
                      <HrTermCalendarItems terms={dayTerms} onOpen={setCalendarTerm} />
                    </Grid.Col>
                  );
                })}
              </Grid>
            </Stack>
          )}
          {!calendarLoading && !calendarError && rows.length === 0 && (
            <Alert color="blue">V zadanom období nie sú žiadne termíny.</Alert>
          )}
        </Stack>
      )}

      {bulkOpened && (
        <HrBulkTermActionModal
          opened
          title={bulkModalTitle}
          terms={bulkTargetRows}
          fileCategories={fileCategories}
          action={bulkAction}
          onClose={closeBulk}
          onSubmit={submitBulk}
        />
      )}

      <Modal opened={calendarTerm !== null} onClose={() => setCalendarTerm(null)} title="Detail HR termínu">
        {calendarTerm && (
          <Stack>
            <P1Regular>
              {calendarTerm.firstName} {calendarTerm.lastName}
            </P1Regular>
            <P2Regular>Organizácia: {calendarTerm.organizationName ?? calendarTerm.organizationId}</P2Regular>
            <P2Regular>
              {departmentText('Položka úrovne:', 'Stredisko:')} {calendarTerm.departmentName}
            </P2Regular>
            <P2Regular>Typ: {calendarTerm.termTypeName}</P2Regular>
            <P2Regular>Stav lehoty: {formatHrDeadlineState(calendarTerm.deadlineState, calendarTerm.status)}</P2Regular>
            <P2Regular>Opakovanie: {formatHrRecurrence(calendarTerm.recurrence)}</P2Regular>
            <HrTermActions
              data={calendarTerm}
              onArchive={archiveTerm}
              onExecute={openExecuteTerm}
              onReactivate={openReactivateTerm}
              showDetail
              detailAsPrimary
              onPairCertificate={setPairTerm}
              onUnpairCertificate={unpairCertificate}
            />
          </Stack>
        )}
      </Modal>

      {executeTerm && (
        <HrExecuteTermModal
          key={`${executeTerm.termId}-${executeTerm.version}`}
          term={executeTerm}
          fileCategories={executeFileCategories}
          opened
          onClose={closeExecuteTerm}
          onExecuted={() => {
            setRefreshToken((value) => value + 1);
          }}
          onConflict={() => {
            closeExecuteTerm();
            setRefreshToken((value) => value + 1);
            notifications.show({
              title: 'Údaje sa medzičasom zmenili',
              message: 'Tabuľka bola obnovená. Otvorte vykonanie termínu znova.',
              color: 'orange',
            });
          }}
        />
      )}

      <HrReactivateTermModal
        term={reactivateTerm}
        onClose={() => setReactivateTerm(null)}
        onReactivated={() => {
          setRefreshToken((value) => value + 1);
        }}
        onConflict={() => {
          setReactivateTerm(null);
          setRefreshToken((value) => value + 1);
          notifications.show({
            title: 'Údaje sa medzičasom zmenili',
            message: 'Tabuľka bola obnovená. Otvorte zaradenie termínu znova.',
            color: 'orange',
          });
        }}
      />

      <HrPairCertificateModal
        term={pairTerm}
        onClose={() => setPairTerm(null)}
        onPaired={() => {
          setRefreshToken((value) => value + 1);
        }}
      />
    </DashboardLayout>
  );
}
